import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "../form-components/Dropdown";
import { getActiveModelVersion, getCostModelNodes, getCostModelEdges } from "../../views/cost-allocation/api/costModelApi";
import { buildOwnerTreeOptions } from "../../views/allocation/ownerTree";

// /cost-model-versions/active has been observed under a few envelope shapes (and the domain
// struct serialises PascalCase) — accept them all so a missing version never silently empties
// the picker.
const extractVersionId = (v) =>
  v?.cost_model_version?.id ?? v?.cost_model_version?.ID ??
  v?.data?.cost_model_version?.id ?? v?.data?.cost_model_version?.ID ??
  v?.id ?? v?.ID ?? null;

const DisabledBox = ({ children }) => (
  <div className="w-full border border-border rounded-ctrl px-3 py-2 text-sm text-text-2 bg-surface-2 cursor-not-allowed">
    {children}
  </div>
);
DisabledBox.propTypes = { children: PropTypes.node };

/**
 * ScopeNodeTreeSelect — value picker for a budget scope clause, rendered as the same indented
 * cost-model tree the allocation owner picker uses (instead of a flat search list). Loads the
 * dimension's active-version nodes + edges and feeds them through buildOwnerTreeOptions so the
 * hierarchy (and the parent → child nesting) is visible while picking an organisation node.
 */
const ScopeNodeTreeSelect = ({ dimensionId, value, label, onChange, placeholder }) => {
  const enabled = dimensionId != null && Number(dimensionId) > 0;

  const versionQ = useQuery(
    ["budget-scope-version", dimensionId],
    () => getActiveModelVersion(Number(dimensionId)),
    { enabled }
  );
  const versionId = extractVersionId(versionQ.data);

  // Edges drive the indentation; a failure falls back to a flat list rather than blocking the
  // picker. Keyed by version so switching dimensions refetches and React Query caches per tree.
  const treeQ = useQuery(
    ["budget-scope-tree", versionId],
    async () => {
      const [nodes, edges] = await Promise.all([
        getCostModelNodes(versionId).then((l) => l || []),
        getCostModelEdges(versionId).then((l) => l || []).catch(() => []),
      ]);
      // No catch-all code passed: budget scope keeps every node selectable (the previous search
      // picker did too), so this is a pure presentation change — flat list → indented tree.
      return buildOwnerTreeOptions(nodes, edges);
    },
    { enabled: versionId != null }
  );

  if (!enabled) return <DisabledBox>{placeholder || "Select a dimension first"}</DisabledBox>;
  if (versionQ.isLoading || (versionId != null && treeQ.isLoading)) return <DisabledBox>Loading owners…</DisabledBox>;

  const options = treeQ.data || [];
  if (options.length === 0) return <DisabledBox>No owners available for this dimension.</DisabledBox>;

  // Resolve the selected option from the tree; fall back to the stored label so an existing
  // selection still shows even if it isn't in the current option set.
  const selected =
    value != null ? (options.find((o) => o.value === value) || { value, label: label || String(value) }) : null;

  return (
    <Dropdown
      options={options}
      selectedValue={selected}
      setSelectedValue={(opt) => onChange(opt)}
      fullWidth
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
    />
  );
};

ScopeNodeTreeSelect.propTypes = {
  dimensionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default ScopeNodeTreeSelect;
